/**
 * Port of C# GeneratePreprocessMetadata.cs - JSON Array Placeholder Processing
 * Parses JSON array placeholders within preprocessed templates
 */

import { CommonUtil } from '../../common/commonUtil.js';
import { JsonArrayPlaceholder } from '../../model/index.js';
import { JsonArraySkipPlaceholder } from '../model/index.js';
import type { PreprocessedTemplate } from '../model/index.js';

/**
 * Parses JSON array placeholders ({{@ArrayName}}...{{/ArrayName}}) and their negative conditions
 */
export function parseJsonArrayPlaceholders(content: string, template: PreprocessedTemplate): void {
    let searchPos = 0;

    while (searchPos < content.length) {
        // Look for {{@
        const openStart = content.indexOf('{{@', searchPos);
        if (openStart === -1) break;

        // Find closing }} for the opening tag
        const openEnd = content.indexOf('}}', openStart + 3);
        if (openEnd === -1) break;

        // Extract array name
        const arrayName = content.substring(openStart + 3, openEnd).trim();
        if (!arrayName || !CommonUtil.isAlphaNumeric(arrayName)) {
            searchPos = openStart + 3;
            continue;
        }

        // Skip HTMLPLACEHOLDER - this is a slot content marker, not an array
        if (arrayName.toUpperCase() === 'HTMLPLACEHOLDER' || arrayName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
            searchPos = openStart + 3;
            continue;
        }

        // Look for matching closing tag {{/ArrayName}}
        const closeTag = '{{/' + arrayName + '}}';
        const openTag = '{{@' + arrayName + '}}';
        const closeStart = CommonUtil.findMatchingCloseTag(content, openEnd + 2, openTag, closeTag);
        if (closeStart === -1) {
            searchPos = openStart + 3;
            continue;
        }

        // Extract block content
        const blockStart = openEnd + 2;
        const blockContent = content.substring(blockStart, closeStart);
        const fullMatch = content.substring(openStart, closeStart + closeTag.length);

        // Parse nested JSON keys within the block content
        const nestedKeys: string[] = [];
        let keySearchPos = 0;
        while (keySearchPos < blockContent.length) {
            const keyStart = blockContent.indexOf('{{$', keySearchPos);
            if (keyStart === -1) break;

            const keyEnd = blockContent.indexOf('}}', keyStart + 3);
            if (keyEnd === -1) break;

            const keyName = blockContent.substring(keyStart + 3, keyEnd).trim();
            if (keyName && CommonUtil.isAlphaNumeric(keyName)) {
                if (!nestedKeys.includes(keyName)) {
                    nestedKeys.push(keyName);
                }
            }
            keySearchPos = keyEnd + 2;
        }

        // Parse nested conditional blocks within this array ({{?Key}} and {{^Key}})
        const nestedArrays: string[] = [];
        let arraySearchPos = 0;
        while (arraySearchPos < blockContent.length) {
            const posStart = blockContent.indexOf('{{?', arraySearchPos);
            const negStart = blockContent.indexOf('{{^', arraySearchPos);
            let nestedArrayStart: number;
            if (posStart === -1 && negStart === -1) break;
            else if (posStart === -1) nestedArrayStart = negStart;
            else if (negStart === -1) nestedArrayStart = posStart;
            else nestedArrayStart = Math.min(posStart, negStart);

            const nestedArrayEnd = blockContent.indexOf('}}', nestedArrayStart + 3);
            if (nestedArrayEnd === -1) break;

            const nestedArrayName = blockContent.substring(nestedArrayStart + 3, nestedArrayEnd).trim();
            if (nestedArrayName && CommonUtil.isAlphaNumeric(nestedArrayName)) {
                if (!nestedArrays.includes(nestedArrayName)) {
                    nestedArrays.push(nestedArrayName);
                }
            }
            arraySearchPos = nestedArrayEnd + 2;
        }

        // Check for paired negative block {{^ArrayName}}...{{/ArrayName}}
        let negativeBlockContent = '';
        let fullMatchWithNegative = fullMatch;
        let hasNegativeBlock = false;
        let isPairedNegative = false;

        const afterPositiveBlock = closeStart + closeTag.length;
        const negativeOpenTag = '{{^' + arrayName + '}}';
        const negativeOpenStart = content.indexOf(negativeOpenTag, afterPositiveBlock);

        if (negativeOpenStart !== -1) {
            // Check if there's only whitespace between positive and negative blocks
            const between = content.substring(afterPositiveBlock, negativeOpenStart);
            if (between.trim().length === 0 && between.length <= 100) {
                isPairedNegative = true;

                // Find the closing tag for the negative block
                const negativeCloseStart = content.indexOf(closeTag, negativeOpenStart + negativeOpenTag.length);
                if (negativeCloseStart !== -1) {
                    hasNegativeBlock = true;
                    const negativeBlockStart = negativeOpenStart + negativeOpenTag.length;
                    negativeBlockContent = content.substring(negativeBlockStart, negativeCloseStart);
                    fullMatchWithNegative = content.substring(openStart, negativeCloseStart + closeTag.length);
                }
            }
        }

        // Create JSON array placeholder structure
        const arrayPlaceholder = new JsonArrayPlaceholder();
        arrayPlaceholder.arrayName = arrayName;
        arrayPlaceholder.startIndex = openStart;
        arrayPlaceholder.endIndex = closeStart + closeTag.length;
        arrayPlaceholder.fullMatch = fullMatch;
        arrayPlaceholder.blockContent = blockContent;
        arrayPlaceholder.negativeBlockContent = negativeBlockContent;
        arrayPlaceholder.fullMatchWithNegative = fullMatchWithNegative;
        arrayPlaceholder.hasNegativeBlock = hasNegativeBlock;
        arrayPlaceholder.isPairedNegative = isPairedNegative;
        arrayPlaceholder.nestedKeys = nestedKeys;
        arrayPlaceholder.nestedArrays = nestedArrays;

        template.jsonArrayPlaceholders.push(arrayPlaceholder);

        // Move past this array block (including negative block if paired)
        searchPos = isPairedNegative && hasNegativeBlock
            ? openStart + fullMatchWithNegative.length
            : closeStart + closeTag.length;
    }
}

/**
 * Parses JSON array skip placeholders ({{!@ArrayName}}...{{/ArrayName}}) from template content
 * These mark array blocks that should be skipped entirely
 */
export function parseJsonArraySkipPlaceholders(content: string, template: PreprocessedTemplate): void {
    // Find all {{!@ArrayName}} skip markers
    let searchPos = 0;
    while (searchPos < content.length) {
        // Find opening skip tag {{!@
        const openStart = content.indexOf('{{!@', searchPos);
        if (openStart === -1) break;

        // Find the end of the opening tag }}
        const openEnd = content.indexOf('}}', openStart + 4);
        if (openEnd === -1) {
            searchPos = openStart + 4;
            continue;
        }

        // Extract array name
        const arrayName = content.substring(openStart + 4, openEnd);
        if (!arrayName) {
            searchPos = openEnd + 2;
            continue;
        }

        // Find closing tag {{/ArrayName}}
        const closeTag = '{{/' + arrayName + '}}';
        const closeStartIdx = content.toLowerCase().indexOf(closeTag.toLowerCase(), openEnd + 2);
        if (closeStartIdx === -1) {
            searchPos = openEnd + 2;
            continue;
        }

        const closeEnd = closeStartIdx + closeTag.length;

        // Check for paired negative block immediately after
        const negativeOpenTag = '{{^' + arrayName + '}}';
        const negativeCloseTag = '{{/' + arrayName + '}}';
        const afterClose = closeEnd;
        const negativeStart = content.toLowerCase().indexOf(negativeOpenTag.toLowerCase(), afterClose);

        let fullEndIndex = closeEnd;

        // Check if negative block is paired (immediately follows with only whitespace)
        if (negativeStart !== -1) {
            const between = content.substring(afterClose, negativeStart);
            if (!between.trim()) {
                // Find end of negative block
                const negativeCloseStart = content.toLowerCase().indexOf(negativeCloseTag.toLowerCase(), negativeStart + negativeOpenTag.length);
                if (negativeCloseStart !== -1) {
                    fullEndIndex = negativeCloseStart + negativeCloseTag.length;
                }
            }
        }

        const fullMatch = content.substring(openStart, fullEndIndex);

        const arraySkipPlaceholder = new JsonArraySkipPlaceholder();
        arraySkipPlaceholder.arrayName = arrayName;
        arraySkipPlaceholder.startIndex = openStart;
        arraySkipPlaceholder.endIndex = fullEndIndex;
        arraySkipPlaceholder.fullMatch = fullMatch;

        template.jsonArraySkipPlaceholders.push(arraySkipPlaceholder);

        // Move past this skip block
        searchPos = fullEndIndex;
    }
}

// #endregion
